import React from 'react';
import { ChevronDown, CheckCircle, Play, XCircle, Music, UserCircle } from 'lucide-react';
import { getAnimeStatus, getAnimeRank } from '../../utils/animeUtils';
import getRankColor from '../../utils/getRankColor';
import StarRating from './StarRating';
import EpisodeRow from './EpisodeRow';
import { useLanguage } from '../../hooks/useLanguage';

const AnimeDetailView = ({ 
  anime, 
  allRatings, 
  onBack, 
  onEdit, 
  onRevive, 
  onRateEpisode 
}) => {
  const { t, language } = useLanguage();
  const status = getAnimeStatus(anime);
  const rank = getAnimeRank(anime, allRatings);

  // 年・季節の表示フォーマット関数
  const formatYearSeason = (year, season) => {
    if (language === 'en') {
      const seasonMap = {
        '春': 'Spring',
        '夏': 'Summer',
        '秋': 'Fall',
        '冬': 'Winter'
      };
      const englishSeason = seasonMap[season] || season;
      return season ? `${year} ${englishSeason}` : `${year}`;
    } else {
      return season ? `${year}年 ${season}クール` : `${year}年`;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-12">
      {/* ヘッダー */}
      <div className="flex items-center mb-4">
        <button
          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
          onClick={onBack}
        >
          <ChevronDown className="rotate-90" />
        </button>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 ml-2">
          {anime.title}
          {anime.subtitle && (
            <span className="ml-2 text-lg font-medium text-gray-500 dark:text-gray-400">{anime.subtitle}</span>
          )}
        </h2>

        {anime.dropped && (
          <button
            onClick={() => onRevive(anime.id)}
            className="ml-4 px-3 py-1 bg-green-600 dark:bg-green-700 text-white text-xs rounded hover:bg-green-700 dark:hover:bg-green-600 transition"
          >
            {t('reviveFromDrop')}
          </button>
        )}
        
        {/* ステータスバッジ */}
        <span className={`ml-3 px-3 py-1 text-xs font-bold rounded-full flex items-center gap-1 ${
          status === 'complete' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
          status === 'watching' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
          status === 'dropped' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
          'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
        }`}>
          {status === 'complete' && <><CheckCircle size={12} /> {t('complete')}</>}
          {status === 'watching' && <><Play size={12} /> {t('watching')}</>}
          {status === 'dropped' && <><XCircle size={12} /> {t('dropped')}</>}
          {status === 'not-started' && t('notStarted')}
        </span>
        
        <span className={`ml-2 px-2 py-1 text-xs font-bold rounded ${getRankColor(rank)}`}>
          {rank || 'N/A'}
        </span>
        
        <button
          onClick={() => onEdit(anime)}
          className="ml-auto px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded hover:bg-blue-100 dark:hover:bg-blue-900/20 text-blue-800 dark:text-blue-400 transition"
        >
          {t('edit')}
        </button>
      </div>

      {/* サムネイル＋基本情報 */}
      <div className="flex flex-col md:flex-row gap-8 mb-6">
        {anime.thumbnail && (
          <img
            src={anime.thumbnail}
            alt="サムネイル"
            className="w-full md:w-64 h-36 md:h-40 object-cover rounded-lg shadow-lg"
          />
        )}
        <div className="flex-1 space-y-2">
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('broadcastYear')}</span> : {formatYearSeason(anime.year, anime.season)}
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('episodes')}</span> : {anime.episodes.length}{t('episodeSuffix')}
            {anime.dropped && (
              <span className="text-red-600 dark:text-red-400 ml-2">
                （{t('droppedAt', { number: anime.droppedAtEpisode })}）
              </span>
            )}
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('ratingMode')}</span> : {anime.ratingMode === 'detailed' ? t('detailedRating') : t('simpleRating')}
          </div>
          {anime.broadcastDay && (
            <div>
              <span className="text-gray-500 dark:text-gray-400">{t('broadcastDay')}</span> : {anime.broadcastDay}
            </div>
          )}
          {anime.streamingSite && (
            <div>
              <span className="text-gray-500 dark:text-gray-400">{t('streamingSite')}</span> : {anime.streamingSite}
            </div>
          )}
          {anime.studio && (
            <div>
              <span className="text-gray-500 dark:text-gray-400">{t('studio')}</span> : {anime.studio}
            </div>
          )}
          {anime.director && (
            <div>
              <span className="text-gray-500 dark:text-gray-400">{t('director')}</span> : {anime.director}
            </div>
          )}
          {anime.officialSite && (
            <div>
              <a
                href={anime.officialSite}
                className="text-blue-600 dark:text-blue-400 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('officialSite')}
              </a>
            </div>
          )}
          {anime.officialX && (
            <div>
              <a href={anime.officialX}
                 target="_blank" rel="noopener noreferrer"
                 className="text-sky-500 dark:text-sky-400 underline">
                 {t('officialX')}
              </a>
            </div>
          )}
          {anime.pvUrl && (
            <div>
              <a
                href={anime.pvUrl}
                className="text-red-600 dark:text-red-400 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('pvWatch')}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* 推しキャラクター */}
      <div className="mb-4">
        <h3 className="text-md font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
          <UserCircle size={16} />
          {t('favoriteCharacters')}
        </h3>
        <div className="flex gap-6 flex-wrap">
          {(anime.favoriteCharacters || []).map(
            (char, i) =>
              char?.name && (
                <div key={i} className="flex flex-col items-center bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  {char.image && (
                    <img
                      src={char.image}
                      alt={char.name}
                      className="h-16 w-16 rounded-full object-cover mb-2"
                    />
                  )}
                  <span className="font-semibold dark:text-gray-100">{char.name}</span>
                  {char.voiceActor && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">{char.voiceActor}</span>
                  )}
                </div>
              )
          )}
        </div>
      </div>

      {/* 主題歌 */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center gap-2 mb-2 text-sm text-gray-500 dark:text-gray-400">
            <Music size={16} /> {t('opening')}
          </div>
          {(anime.opSongs || [anime.opSong]).filter(Boolean).map((song, index) => (
            <div key={index} className="mb-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold dark:text-gray-100 truncate">{song?.title || t('registered')}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{song?.artist}</div>
                </div>
                <div className="flex-shrink-0">
                  <StarRating rating={song?.rating || 0} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2 text-sm text-gray-500 dark:text-gray-400">
            <Music size={16} /> {t('ending')}
          </div>
          {(anime.edSongs || [anime.edSong]).filter(Boolean).map((song, index) => (
            <div key={index} className="mb-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold dark:text-gray-100 truncate">{song?.title || t('registered')}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{song?.artist}</div>
                </div>
                <div className="flex-shrink-0">
                  <StarRating rating={song?.rating || 0} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 各話一覧 */}
      <div>
        <h3 className="text-md font-bold text-gray-700 dark:text-gray-300 mb-2">{t('episodeList')}</h3>
        {anime.episodes.map((ep) => (
          <EpisodeRow
            key={ep.id}
            episode={ep}
            onRate={onRateEpisode}
            ratingMode={anime.ratingMode}
            isDropped={anime.dropped && ep.number > anime.droppedAtEpisode}
          />
        ))}
      </div>
    </div>
  );
};

export default AnimeDetailView;